import { useState, useEffect, useRef } from 'react'
import { FilterIcon } from '../icons'
import Button from '../buttons/button'
import RadioInput from './radioInput'
import TypographyLabel from '../typography/TypographyLabel'
import clsx from 'clsx'

interface FilterOption {
	title: string
	description: string
	optionCount?: number
}

interface MobileFilterProps {
	sortOptions: FilterOption[]
	filterOptions: FilterOption[]
	onSortChange: (option: FilterOption) => void
	onFilterChange: (option: FilterOption) => void
	selectedSort?: FilterOption
	selectedFilter?: FilterOption
}

const MobileFilter = ({
	sortOptions,
	filterOptions,
	onSortChange,
	onFilterChange,
	selectedSort,
	selectedFilter,
}: MobileFilterProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [tempSelectedSort, setTempSelectedSort] = useState<
		FilterOption | undefined
	>(selectedSort)
	const [tempSelectedFilter, setTempSelectedFilter] = useState<
		FilterOption | undefined
	>(selectedFilter)
	const containerRef = useRef<HTMLDivElement>(null)

	// Reset temp selections when selected values change from parent
	useEffect(() => {
		setTempSelectedSort(selectedSort)
		setTempSelectedFilter(selectedFilter)
	}, [selectedSort, selectedFilter])

	// Handle click outside to close the filter
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				handleCancel()
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleApply = () => {
		if (tempSelectedSort) {
			onSortChange(tempSelectedSort)
		}
		if (tempSelectedFilter) {
			onFilterChange(tempSelectedFilter)
		}
		setIsOpen(false)
	}

	const handleCancel = () => {
		setTempSelectedSort(selectedSort)
		setTempSelectedFilter(selectedFilter)
		setIsOpen(false)
	}

	return (
		<div className="relative" ref={containerRef}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className={clsx(
					'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border transition-colors',
					isOpen
						? 'bg-uiTertiaryDark text-textPrimaryDark dark:bg-uiTertiaryLight dark:text-textSecondaryLight'
						: 'border-textInactiveLight text-textPrimaryLight hover:border-textPrimaryLight hover:bg-uiTertiaryDark hover:text-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryDark dark:hover:bg-textPrimaryLight dark:hover:text-textPrimaryDark',
				)}
			>
				<FilterIcon />
			</button>

			{isOpen && (
				<div className="absolute right-0 z-50 mt-2 w-72 origin-top-right overflow-hidden rounded-2xl bg-uiTertiaryDark shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-light">
					<div className="pb-1 pt-4">
						<TypographyLabel
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryDark dark:text-textPrimaryLight px-4"
						>
							Sort by
						</TypographyLabel>
						<div className="">
							{sortOptions.map(option => (
								<div
									key={option.title}
									onClick={() => setTempSelectedSort(option)}
									className={
										tempSelectedSort?.title === option.title
											? 'bg-uiSecondaryDark dark:bg-uiTertiaryLight'
											: ''
									}
								>
									<div className="px-2">
										<RadioInput
											size="large"
											radioSize="small"
											name={option.title}
											current={tempSelectedSort?.title === option.title}
											invertTheme={true}
										/>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="border-t border-uiSecondaryDark pb-4 pt-1 dark:border-uiSecondaryLight">
						<TypographyLabel
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryDark dark:text-textPrimaryLight px-4"
						>
							Attestation Type
						</TypographyLabel>
						<div className="">
							{filterOptions
								.filter(option => option.optionCount !== 0)
								.map(option => (
									<div
										key={option.title}
										onClick={() => setTempSelectedFilter(option)}
										className={
											tempSelectedFilter?.title === option.title
												? 'bg-uiSecondaryDark dark:bg-uiTertiaryLight'
												: ''
										}
									>
										<div className="px-2">
											<RadioInput
												size="large"
												radioSize="small"
												name={`${option.title}${option.optionCount !== undefined ? ` (${option.optionCount})` : ''}`}
												current={tempSelectedFilter?.title === option.title}
												isInactive={false}
												invertTheme={true}
											/>
										</div>
									</div>
								))}
						</div>
					</div>
					<div className="flex justify-end gap-x-2 border-t border-uiSecondaryDark p-4 dark:border-uiSecondaryLight">
						<Button variant="primary" size="small" onClick={handleCancel}>
							Cancel
						</Button>
						<Button variant="primary" size="small" onClick={handleApply}>
							Apply
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}

export default MobileFilter
